<!-- A speaker's slides (the Program, People highlights, video pages): a PDF icon, shown to
     everyone. Downloading needs sign-in (2026-10-14): signed out, Sign-In opens saying so
     ("Downloading this file is free but requires you to sign in") and the download follows once
     signed in. And it follows the speaker's video (2026-10-24): where that video is locked, the
     slides are for the accounts that may watch it; the sign-in then names the event, and a
     signed-in account without access goes to #register for it, as on the video's page. The file's
     address is unguessable and comes from the fullstack (useSlidesIndex.js; the same decision as
     the videos'); it's saved under the speaker's name. -->
<template>
  <button
    v-if="slidesKey"
    type="button"
    :class="align"
    :aria-label="`Download ${name}'s slides (PDF)`"
    title="Download slides"
    @click.stop.prevent="download">
    <img :src="pdfIcon" alt="" />
  </button>
</template>

<script setup>
const log = (msg) => process.env.VUE_APP_DEV && console.log(`Slides: ${msg}`); // eslint-disable-line
const props = defineProps(["name", "year", "align"]); // eslint-disable-line
import {computed, shallowRef, watch} from "vue";
import pdfIcon from "@/assets/img/pdf.svg";
import {useRoute, useRouter} from "vue-router";
import useUserSafe from "@/composables/useUserSafe";
import {requestSignIn} from "@/composables/useSignInRequest";
import {redirectToRegistration} from "@/composables/useRedirectToRegistration";
import {fetchSlidesLookup, getSlidesKey, loadSlidesIndex} from "@/composables/useSlidesIndex";

// a download from a same-site address: saved as "<Name>.pdf", not the unguessable file name
function saveAs(path, fileName) {
  const link = document.createElement("a");
  link.href = path;
  link.download = fileName;
  document.body.append(link);
  link.click();
  link.remove();
}

const slidesKey = shallowRef(null);
const route = useRoute();
const router = useRouter();
const {isSignedIn} = useUserSafe();
const year = computed(() => {
  let output = "";
  if (route.params.year) output = route.params.year;
  if (props?.year) output = props.year;
  return output;
});

async function resolveSlides(yearValue) {
  if (!yearValue || !props.name) return;
  const index = await loadSlidesIndex(yearValue);
  slidesKey.value = getSlidesKey(index, props.name);
}

// the sign-in's words: the event's, when the slides are locked (useSignInRequest.js)
const SIGN_IN_REASONS = {masterclass: "lockedSlidesMasterclass", workshop: "lockedSlidesWorkshop"};

async function download() {
  const data = await fetchSlidesLookup(year.value, slidesKey.value);
  if (data.ok) return saveAs(data.path, `${props.name.replace(/ /g, "_")}.pdf`);
  if (data.action === "register") return redirectToRegistration(data.event);
  if (data.action === "login" && !isSignedIn.value) {
    return requestSignIn(router, SIGN_IN_REASONS[data.event] || "slides", download);
  }
  log(`no address for ${year.value}/${slidesKey.value}: ${data.action}`);
}

watch(year, resolveSlides, {immediate: true});
</script>

<style scoped>
/* an icon button: global.css's button look reset */
button {
  margin: 0 0 0 0.5em;
  padding: 0;
  border: none;
  border-radius: 0;
  box-shadow: none;
  background: none;
  line-height: 1;
  cursor: pointer;
  vertical-align: middle;
}
button.left {
  margin: 0 0.5em 0 0;
}
button:focus-visible {
  outline: 2px solid var(--link-color-subtle);
  outline-offset: 2px;
}
img {
  height: 14px;
  vertical-align: middle; /* Vertically align with text */
}
.narrow img {
  height: 13px;
}
</style>
